import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

import PageTitle from "../../components/Page Component/PageTitle";
import { RootState } from "../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  PostDataSuccessAction,
  PostDataFailureAction,
  PutDataSuccessAction,
  PutDataFailureAction,
  DeleteDataSuccessAction,
  DeleteDataFailureAction,
  fetchData,
  postData,
  putData,
  deleteData,
} from "../../store/actions";
import { useCustomToast } from "../../components/Methods/SnackBar";
import { FEEDBACK_TEMPLATES_API } from "../../Service/Apis";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A single rating criterion within a feedback template.
 * `parameter_id` is absent for parameters that have not yet been persisted
 * (i.e. newly added rows in the editor that haven't been POSTed yet).
 */
interface Parameter {
  parameter_id?: string;
  label: string;
  /** Short guidance text shown to the client beneath the label on the feedback form. */
  help_text: string;
  /** When true, the client must provide a rating for this parameter before submitting. */
  is_mandatory: boolean;
  /** 1-based display order used to sort parameters on both the editor and the public form. */
  order: number;
}

/**
 * Full representation of a feedback template including its nested parameters.
 * The template controls the structure of the public-facing feedback form sent to clients.
 */
interface FeedbackTemplate {
  template_id: string;
  name: string;
  /** Maximum star value (5 or 10) shown on the client's rating interface. */
  rating_scale: number;
  /** When true, the "Additional Comments" field becomes required for the client. */
  comments_mandatory: boolean;
  /** When true, the client's name and email are hidden from the internal response view. */
  is_anonymous: boolean;
  /** Number of days after which the one-time feedback link automatically expires. */
  expiry_days: number;
  /**
   * Ratings at or below this value trigger an internal low-rating alert, allowing
   * managers to follow up with the client proactively.
   */
  low_rating_threshold: number;
  /** Inactive templates are excluded from the dropdown when creating feedback requests. */
  is_active: boolean;
  parameters: Parameter[];
}

/**
 * Returns sensible defaults for a brand-new template.
 * Extracted as a factory to ensure fresh object references each time the dialog opens.
 */
const emptyTemplate = (): Omit<FeedbackTemplate, "template_id"> => ({
  name: "",
  rating_scale: 5,
  comments_mandatory: false,
  is_anonymous: false,
  expiry_days: 7,
  low_rating_threshold: 2,
  is_active: true,
  parameters: [],
});

/**
 * Returns a blank parameter row ready to be edited in the template editor.
 * `order` is intentionally 0 here and gets recalculated when appended to the list.
 */
const emptyParameter = (): Parameter => ({
  label: "",
  help_text: "",
  is_mandatory: true,
  order: 0,
});

// ---------------------------------------------------------------------------
// Template Editor Dialog
// ---------------------------------------------------------------------------

/** Props for the create/edit template dialog. Pass `template` as null to create a new one. */
interface TemplateDialogProps {
  open: boolean;
  /** Existing template to edit. When null the dialog operates in "create" mode. */
  template: FeedbackTemplate | null;
  onClose: () => void;
  /** Invoked after a successful create or update so the parent can refresh its list. */
  onSaved: () => void;
}

/**
 * Full-screen dialog used to create a new feedback template or edit an existing one.
 *
 * Parameter sync strategy on edit:
 * - Parameters removed from the form are deleted via the sub-resource endpoint.
 * - Parameters with an existing `parameter_id` are PUT (updated).
 * - Parameters without an id (newly added rows) are POST-ed as new sub-resources.
 * This diff-based approach keeps the API calls minimal and avoids wholesale
 * replacement of the parameter set.
 *
 * Drag-and-drop reordering is implemented via native HTML5 drag events; the `dragIdx`
 * and `dragOverIdx` state values track the source and destination rows respectively.
 */
const TemplateDialog: React.FC<TemplateDialogProps> = ({
  open,
  template,
  onClose,
  onSaved,
}) => {
  const isEdit = !!template?.template_id;

  const [form, setForm] = useState<Omit<FeedbackTemplate, "template_id">>(
    emptyTemplate()
  );
  const [saving, setSaving] = useState<boolean>(false);
  const [nameError, setNameError] = useState<string>("");
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const showToast = useCustomToast();

  const dispatch: ThunkDispatch<
    RootState,
    null,
    | FetchDataSuccessAction
    | FetchDataFailureAction
    | PostDataSuccessAction
    | PostDataFailureAction
    | PutDataSuccessAction
    | PutDataFailureAction
    | DeleteDataSuccessAction
    | DeleteDataFailureAction
  > = useDispatch();

  useEffect(() => {
    if (!open) return;
    if (template) {
      const { template_id: _, ...rest } = template;
      setForm({ ...rest, parameters: template.parameters.map((p) => ({ ...p })) });
    } else {
      setForm(emptyTemplate());
    }
    setNameError("");
  }, [open, template]);

  /** Updates a top-level template field (name, rating scale, toggles, etc.). */
  const handleFieldChange = (field: keyof typeof form, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Updates a single field of the parameter at position `idx`.
   * Produces a new array reference so React detects the change and re-renders.
   */
  const handleParamChange = (
    idx: number,
    field: keyof Parameter,
    value: any
  ) => {
    setForm((prev) => {
      const params = [...prev.parameters];
      params[idx] = { ...params[idx], [field]: value };
      return { ...prev, parameters: params };
    });
  };

  /** Appends a blank parameter row to the end of the list and assigns it the next order number. */
  const addParameter = () => {
    setForm((prev) => ({
      ...prev,
      parameters: [
        ...prev.parameters,
        { ...emptyParameter(), order: prev.parameters.length + 1 },
      ],
    }));
  };

  /**
   * Removes the parameter at `idx` from the local form state and
   * recalculates `order` values for all remaining rows to keep them contiguous.
   * The actual DELETE API call happens later inside `handleSave`.
   */
  const removeParameter = (idx: number) => {
    setForm((prev) => ({
      ...prev,
      parameters: prev.parameters
        .filter((_, i) => i !== idx)
        .map((p, i) => ({ ...p, order: i + 1 })),
    }));
  };

  /**
   * Swaps the parameter at `idx` with its neighbour in `direction` (-1 = up, 1 = down).
   * Serves as a keyboard-friendly alternative to drag-and-drop reordering.
   */
  const moveParameter = (idx: number, direction: -1 | 1) => {
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= form.parameters.length) return;
    setForm((prev) => {
      const params = [...prev.parameters];
      [params[idx], params[newIdx]] = [params[newIdx], params[idx]];
      return {
        ...prev,
        parameters: params.map((p, i) => ({ ...p, order: i + 1 })),
      };
    });
  };

  /**
   * Persists the template and its parameters.
   * Parameters are stripped from the main template payload because the backend's
   * `FeedbackTemplateSerializer.parameters` field is read-only; they are managed
   * through a separate nested resource endpoint (`/parameters/`).
   */
  const handleSave = async () => {
    if (!form.name.trim()) {
      setNameError("Template name is required.");
      return;
    }
    setSaving(true);
    try {
      // Strip parameters from the template payload — they are managed separately
      // via the sub-resource endpoint because FeedbackTemplateSerializer.parameters
      // is read_only.
      const { parameters: formParams, ...templateMeta } = form;
      const orderedParams = formParams.map((p, i) => ({ ...p, order: i + 1 }));

      let templateId: string;

      if (isEdit && template?.template_id) {
        await dispatch(putData(`${FEEDBACK_TEMPLATES_API}${template.template_id}/`, templateMeta as any));
        templateId = template.template_id;

        // Sync parameters: determine adds, updates, deletes
        const originalIds = new Set(
          (template.parameters ?? [])
            .filter((p) => p.parameter_id)
            .map((p) => p.parameter_id as string)
        );
        const newIds = new Set(
          orderedParams.filter((p) => p.parameter_id).map((p) => p.parameter_id as string)
        );

        // Delete parameters removed from the form
        for (const oldId of originalIds) {
          if (!newIds.has(oldId)) {
            await dispatch(
              deleteData(`${FEEDBACK_TEMPLATES_API}${templateId}/parameters/${oldId}/`)
            );
          }
        }

        // Create or update remaining parameters
        for (const param of orderedParams) {
          if (param.parameter_id && originalIds.has(param.parameter_id)) {
            // Update existing
            await dispatch(
              putData(
                `${FEEDBACK_TEMPLATES_API}${templateId}/parameters/${param.parameter_id}/`,
                { label: param.label, help_text: param.help_text, is_mandatory: param.is_mandatory, order: param.order } as any
              )
            );
          } else {
            // Create new
            await dispatch(
              postData(`${FEEDBACK_TEMPLATES_API}${templateId}/parameters/`, {
                label: param.label,
                help_text: param.help_text,
                is_mandatory: param.is_mandatory,
                order: param.order,
              } as any)
            );
          }
        }

        showToast("Template updated.", "success", false);
      } else {
        const created: any = await dispatch(postData(FEEDBACK_TEMPLATES_API, templateMeta as any));
        templateId = created.template_id;

        // Add all parameters
        for (const param of orderedParams) {
          await dispatch(
            postData(`${FEEDBACK_TEMPLATES_API}${templateId}/parameters/`, {
              label: param.label,
              help_text: param.help_text,
              is_mandatory: param.is_mandatory,
              order: param.order,
            } as any)
          );
        }

        showToast("Template created.", "success", false);
      }
      onSaved();
    } catch {
      showToast("Failed to save template.", "error", false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{ sx: { height: "92vh", maxHeight: "92vh" } }}
    >
      <DialogTitle>
        {isEdit ? "Edit Feedback Template" : "New Feedback Template"}
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
          {/* Template meta */}
          <TextField
            label="Template Name *"
            size="small"
            fullWidth
            value={form.name}
            onChange={(e) => {
              handleFieldChange("name", e.target.value);
              setNameError("");
            }}
            error={!!nameError}
            helperText={nameError}
          />

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
            {/* Rating Scale — only 5 or 10 */}
            <TextField
              label="Rating Scale (5 or 10)"
              size="small"
              value={form.rating_scale}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                if (v === 5 || v === 10) handleFieldChange("rating_scale", v);
              }}
              sx={{ width: 210 }}
              inputProps={{ inputMode: "numeric", style: { textAlign: "center" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleFieldChange(
                          "rating_scale",
                          form.rating_scale === 10 ? 5 : form.rating_scale
                        )
                      }
                      disabled={form.rating_scale === 5}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleFieldChange(
                          "rating_scale",
                          form.rating_scale === 5 ? 10 : form.rating_scale
                        )
                      }
                      disabled={form.rating_scale === 10}
                    >
                      <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Expiry Days */}
            <TextField
              label="Expiry Days"
              size="small"
              value={form.expiry_days}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                if (!isNaN(v) && v >= 1) handleFieldChange("expiry_days", v);
              }}
              sx={{ width: 180 }}
              inputProps={{ inputMode: "numeric", style: { textAlign: "center" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleFieldChange(
                          "expiry_days",
                          Math.max(1, form.expiry_days - 1)
                        )
                      }
                      disabled={form.expiry_days <= 1}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleFieldChange("expiry_days", form.expiry_days + 1)
                      }
                    >
                      <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Low Rating Threshold */}
            <TextField
              label="Low Rating Threshold"
              size="small"
              value={form.low_rating_threshold}
              onChange={(e) => {
                const v = parseInt(e.target.value);
                if (!isNaN(v) && v >= 1 && v <= form.rating_scale)
                  handleFieldChange("low_rating_threshold", v);
              }}
              sx={{ width: 220 }}
              inputProps={{ inputMode: "numeric", style: { textAlign: "center" } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleFieldChange(
                          "low_rating_threshold",
                          Math.max(1, form.low_rating_threshold - 1)
                        )
                      }
                      disabled={form.low_rating_threshold <= 1}
                    >
                      <RemoveCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleFieldChange(
                          "low_rating_threshold",
                          Math.min(form.rating_scale, form.low_rating_threshold + 1)
                        )
                      }
                      disabled={form.low_rating_threshold >= form.rating_scale}
                    >
                      <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.comments_mandatory}
                  onChange={(e) =>
                    handleFieldChange("comments_mandatory", e.target.checked)
                  }
                />
              }
              label="Comments Mandatory"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_anonymous}
                  onChange={(e) =>
                    handleFieldChange("is_anonymous", e.target.checked)
                  }
                />
              }
              label="Anonymous Feedback"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) =>
                    handleFieldChange("is_active", e.target.checked)
                  }
                />
              }
              label="Active"
            />
          </Box>

          <Divider />

          {/* Parameters */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Parameters
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addParameter}
              >
                Add Parameter
              </Button>
            </Box>

            {form.parameters.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No parameters added yet.
              </Typography>
            ) : (
              <Table size="small" sx={{ tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: 60 }} />
                  <col style={{ width: "25%" }} />
                  <col />
                  <col style={{ width: 110 }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 50 }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>Sr. No.</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>
                      Feedback Topic{" "}
                      <Typography component="span" sx={{ color: "error.main" }}>
                        *
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>Question</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>Mandatory</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>Display Order</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {form.parameters.map((param, idx) => (
                    <TableRow
                      key={idx}
                      draggable
                      onDragStart={() => {
                        setDragIdx(idx);
                        setDragOverIdx(idx);
                      }}
                      onDragEnter={() => {
                        if (dragIdx === null || dragIdx === idx) return;
                        setDragOverIdx(idx);
                        setForm((prev) => {
                          const params = [...prev.parameters];
                          const [moved] = params.splice(dragIdx, 1);
                          params.splice(idx, 0, moved);
                          setDragIdx(idx);
                          return {
                            ...prev,
                            parameters: params.map((p, i) => ({ ...p, order: i + 1 })),
                          };
                        });
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDragEnd={() => {
                        setDragIdx(null);
                        setDragOverIdx(null);
                      }}
                      sx={{
                        opacity: dragIdx === idx ? 0.4 : 1,
                        backgroundColor:
                          dragOverIdx === idx && dragIdx !== idx
                            ? "action.hover"
                            : "inherit",
                        cursor: "grab",
                        "&:active": { cursor: "grabbing" },
                      }}
                    >
                      <TableCell>
                        <DragHandleIcon
                          fontSize="small"
                          sx={{ color: "text.secondary", verticalAlign: "middle" }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={param.label}
                          onChange={(e) =>
                            handleParamChange(idx, "label", e.target.value)
                          }
                          placeholder="e.g. Communication, Responsiveness…"
                          fullWidth
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={param.help_text}
                          onChange={(e) =>
                            handleParamChange(idx, "help_text", e.target.value)
                          }
                          placeholder="Brief description (e.g. Rate how clearly we communicated)"
                          fullWidth
                          multiline
                          minRows={1}
                          maxRows={3}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      </TableCell>
                      <TableCell>
                        <Switch
                          size="small"
                          checked={param.is_mandatory}
                          onChange={(e) =>
                            handleParamChange(
                              idx,
                              "is_mandatory",
                              e.target.checked
                            )
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            disabled={idx === 0}
                            onClick={() => moveParameter(idx, -1)}
                          >
                            ↑
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={idx === form.parameters.length - 1}
                            onClick={() => moveParameter(idx, 1)}
                          >
                            ↓
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeParameter(idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? "Saving…" : "Save Template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

/**
 * Admin page for managing the library of reusable feedback templates.
 *
 * Each template is rendered as a collapsible accordion showing its configuration
 * summary and a read-only parameter table. Editing and deletion are protected by
 * a confirmation dialog to prevent accidental data loss.
 *
 * Template activation/deactivation is handled within the edit dialog rather than
 * inline on this page, keeping the list view uncluttered.
 */
const FeedbackTemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<FeedbackTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [editTarget, setEditTarget] = useState<FeedbackTemplate | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const showToast = useCustomToast();

  const dispatch: ThunkDispatch<
    RootState,
    null,
    | FetchDataSuccessAction
    | FetchDataFailureAction
    | DeleteDataSuccessAction
    | DeleteDataFailureAction
  > = useDispatch();

  /** Loads all templates from the API and replaces the local list in state. */
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dispatch(fetchData(FEEDBACK_TEMPLATES_API));
      setTemplates(Array.isArray(data) ? data : data?.results ?? []);
    } catch {
      showToast("Failed to load templates.", "error", false);
    } finally {
      setLoading(false);
    }
  }, [dispatch, showToast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  /** Opens the template editor in "create" mode with no pre-populated data. */
  const openCreate = () => {
    setEditTarget(null);
    setDialogOpen(true);
  };

  /** Opens the template editor pre-populated with the selected template's current values. */
  const openEdit = (t: FeedbackTemplate) => {
    setEditTarget(t);
    setDialogOpen(true);
  };

  /**
   * Permanently deletes a template after the user confirms the action.
   * The template is removed from local state optimistically only after the API
   * call succeeds to avoid showing a stale empty slot on failure.
   */
  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await dispatch(deleteData(`${FEEDBACK_TEMPLATES_API}${id}/`));
      showToast("Template deleted.", "success", false);
      setTemplates((prev) => prev.filter((t) => t.template_id !== id));
    } catch {
      showToast("Failed to delete template.", "error", false);
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Container maxWidth="lg" sx={{ mt: 12, mb: 10 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1,
        }}
      >
        <PageTitle>Feedback Template Manager</PageTitle>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ mt: 1 }}
        >
          New Template
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : templates.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 4 }}>
          No templates found. Create one to get started.
        </Typography>
      ) : (
        templates.map((tmpl) => (
          <Accordion
            key={tmpl.template_id}
            sx={{
              mb: 2,
              border: "1px solid",
              borderColor: tmpl.is_active ? "primary.main" : "divider",
              borderRadius: "8px !important",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flex: 1,
                  pr: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography fontWeight={600}>{tmpl.name}</Typography>
                <Chip
                  label={tmpl.is_active ? "Active" : "Inactive"}
                  color={tmpl.is_active ? "success" : "default"}
                  size="small"
                />
                <Typography variant="body2" color="text.secondary">
                  Scale: {tmpl.rating_scale} | Expiry: {tmpl.expiry_days}d |{" "}
                  {tmpl.parameters.length} parameter
                  {tmpl.parameters.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            </AccordionSummary>

            <AccordionDetails>
              {/* Config summary */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {tmpl.comments_mandatory && (
                  <Chip label="Comments Mandatory" size="small" variant="outlined" />
                )}
                {tmpl.is_anonymous && (
                  <Chip label="Anonymous" size="small" variant="outlined" />
                )}
                <Chip
                  label={`⚠ Low Rating Alert ≤ ${tmpl.low_rating_threshold}`}
                  size="small"
                  sx={{
                    backgroundColor: "#fff8e1",
                    color: "#e65100",
                    borderColor: "#ffb300",
                    border: "1px solid",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    letterSpacing: "0.01em",
                  }}
                />
              </Box>

              {/* Parameters table */}
              {tmpl.parameters.length > 0 && (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>Display Order</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>Feedback Topic</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>Question</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 700 }}>Mandatory</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...tmpl.parameters]
                      .sort((a, b) => a.order - b.order)
                      .map((param, idx) => (
                        <TableRow key={param.parameter_id ?? idx}>
                          <TableCell>{param.order}</TableCell>
                          <TableCell>{param.label}</TableCell>
                          <TableCell>
                            {param.help_text || (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {param.is_mandatory ? (
                              <Chip label="Yes" color="primary" size="small" />
                            ) : (
                              <Chip
                                label="No"
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}

              {/* Actions */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => openEdit(tmpl)}
                >
                  Edit
                </Button>
                <Tooltip title="Delete template">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      disabled={deletingId === tmpl.template_id}
                      onClick={() => setConfirmDeleteId(tmpl.template_id)}
                    >
                      {deletingId === tmpl.template_id ? (
                        <CircularProgress size={14} />
                      ) : (
                        "Delete"
                      )}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      {/* Create / Edit dialog */}
      <TemplateDialog
        open={dialogOpen}
        template={editTarget}
        onClose={() => setDialogOpen(false)}
        onSaved={() => {
          setDialogOpen(false);
          fetchTemplates();
        }}
      />

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this template? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() =>
              confirmDeleteId && handleDelete(confirmDeleteId)
            }
            disabled={!!deletingId}
          >
            {deletingId ? <CircularProgress size={16} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FeedbackTemplateManager;
